import Hue from '@assets/hue_hero.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { IImageWithLink } from '@models/IImageWithLink';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import { IVideo } from '@models/IVideo';
import HighlightCard from '@molecules/HighlightCard/HighlightCard';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import StripCarousel from '@molecules/StripCarousel/StripCarousel';
import VideoModal from '@molecules/VideoModal/VideoModal';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import classNames from 'classnames';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly title: string;
  readonly subTitle?: string;
  readonly primaryCta?: ILink;
  readonly media: IImage;
  readonly highlights?: IImageWithLink[];
  readonly meta?: IMeta;
  readonly video?: {
    readonly videoCTALabel: string;
    readonly video: IVideo;
  };
}

const ImpactHero = ({
  highlights = [],
  media,
  meta,
  primaryCta,
  subTitle,
  title,
  video,
}: Props) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  return (
    <SoftLoadWrapper>
      <section className="component-padding dark relative overflow-hidden bg-black-100 pt-20 text-white">
        <div
          className={classNames(
            'container flex min-h-[375px] flex-col items-center md:min-h-[570px] lg:min-h-[300px] lg:flex-row xl:min-h-[450px] 2xl:min-h-[600px]',
            { 'mb-13 lg:mb-[5.25rem]': !!highlights?.length }
          )}
        >
          <div
            className="relative z-20 mb-10 w-full lg:mb-0 lg:w-7/12"
            data-epi-property-name={meta?.editHints?.media}
          >
            <Image
              image={media}
              imageClasses="h-full w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
              lazyLoad={false}
              dataTestId="CT-04A-Hero-Image"
            />
          </div>

          <div className="mt-auto flex flex-col items-center text-center lg:mt-0 lg:ml-auto lg:w-4/12 lg:items-start lg:text-left">
            <h1
              data-testid="CT-04A-Title"
              data-epi-property-name={meta?.editHints?.title}
            >
              {title}
            </h1>
            {subTitle && (
              <p
                className="h1"
                data-epi-property-name={meta?.editHints?.subTitle}
                data-testid="CT-04A-Subtitle"
              >
                {subTitle}
              </p>
            )}

            {primaryCta?.url && primaryCta?.name && !video && (
              <Button
                link={primaryCta}
                linkCallback={() => {
                  if (pageObject?.pageType === 'HomepagePageViewModel') {
                    updateDataLayer({
                      event: 'selectContent',
                      content_type: 'impact_hero',
                      link_text: primaryCta.name,
                    });
                  }
                }}
                className="mt-4 lg:mt-10"
                dataTestId="CT-04A-Primary-CTA"
              />
            )}

            {video?.video?.url && video.videoCTALabel && (
              <>
                <Button
                  onClick={() => {
                    setIsVideoModalOpen(true);
                  }}
                  className="mt-4 lg:mt-10"
                  dataTestId="CT-04A-Primary-Video-CTA"
                >
                  {video.videoCTALabel}
                </Button>

                <VideoModal
                  videoUrl={video.video?.url}
                  videoTitle={video.videoCTALabel}
                  isOpen={isVideoModalOpen}
                  onOpenChange={setIsVideoModalOpen}
                  shouldCloseOnClickOutsideContent={false}
                  transparent={false}
                  dataTestId="CT-33-Campaign-Intro-Video-Modal"
                />
              </>
            )}
          </div>
        </div>

        {!!highlights?.length && (
          <div className="relative z-20">
            <StripCarousel
              id={`impact-hero-carousel-${meta?.contentLink ?? ''}`}
              items={highlights}
              autoplay={{
                delay: 8000,
              }}
              loop
            >
              {(itemsToDisplay) =>
                itemsToDisplay.map((highlight, index) => (
                  <SwiperSlide
                    className="stretch-card"
                    key={slugify(
                      `highlight-card-${highlight.simpleLink?.url}-${highlight.simpleLink?.name}-${index})`
                    )}
                  >
                    <HighlightCard {...highlight} index={index} />
                  </SwiperSlide>
                ))
              }
            </StripCarousel>
          </div>
        )}

        <div className="-top-[5%] md:-top-[15%] xl:-top-[10%] pointer-events-none absolute left-0 z-10 flex items-center justify-center lg:top-[0] lg:left-[5%] lg:w-[50%]">
          <img src={`${Hue}`} alt="Hue" className="h-full w-full" />
        </div>
      </section>
    </SoftLoadWrapper>
  );
};

export default ImpactHero;
